import classNames from 'classnames';
import {
  type ChordSubgroup,
  type Chords,
  type Chord as D3Chord,
  type ChordGroup as D3ChordGroup,
  ribbon as ribbonFactory,
} from 'd3';
import { useId, useRef, useState } from 'react';

import { useResizeObserver } from '@revelio/core';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import { getFormatter, getTextWidth } from '../../utilities';
import {
  plotColors as defaultPlotColors,
  PRIMARY_COLOR,
} from '../../utilities/plot-colors';
import { plotTestId } from '../../utilities/plotTestId';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import styles from './chord-chart.module.css';
import { ChordGroup, type GroupTick, getLabelRadius } from './chord-group';
import { ChordRibbon } from './chord-ribbon';
import type {
  ChordData,
  ChordFlowMode,
  ChordShareDirection,
  ChordTickLabelMode,
} from './types';

const CHORD_PADDING = 80;
const ARC_THICKNESS = 12;
const LABEL_OFFSET = 6;
const DEFAULT_LABEL_FONT_SIZE = 14;
const TICK_FONT_SIZE = 10;
const TICK_LINE_LENGTH = 4;
const TICK_LABEL_OFFSET = 4;
const TICK_RESERVED_OFFSET = 32;
const PAD_ANGLE = 0.04;
const TARGET_TICKS_PER_LARGEST_GROUP = 4;
const LABEL_FONT_FAMILY = '"Source Sans Pro", sans-serif';
const LABEL_GAP_PX = 6;
const LABEL_DESIRED_PADDING_PX = 4;
const LABEL_RELAX_ITERATIONS = 24;
const LABEL_RELAX_DAMPING = 0.5;

const sharePercentFormat = getFormatter(FormatType.PERCENTAGE);

export type ChordChartProps = {
  data: ChordData | null;
  colors?: string[];
  labelFontSize?: number;
  showTickLabels?: boolean;
  tickLabelMode?: ChordTickLabelMode;
  /**
   * Names of entities to suggest when there are >= 2 selected entities but no
   * flows between them. Shown in the center of the ring alongside the empty
   * state message; pass an empty array (or omit) to show only the message.
   */
  emptyStateSuggestions?: string[];
  /**
   * `'net'` adjusts tooltip labels to "Net Inflow"/"Net Outflow"/"Net
   * Transitions", hides the zero side of each node, and drops the share row
   * (shares don't map to residual net values). Defaults to `'directional'`.
   */
  flowMode?: ChordFlowMode;
} & CommonPlotProps;

type HoverTarget =
  | { kind: 'group'; index: number }
  | { kind: 'ribbon'; sourceIndex: number; targetIndex: number }
  | null;

const hasMeaningfulFlows = (matrix: number[][]): boolean =>
  matrix.some((row) => row.some((value) => value > 0));

export const ChordChart = ({
  data,
  loading = false,
  colors,
  labelFontSize = DEFAULT_LABEL_FONT_SIZE,
  showTickLabels = false,
  tickLabelMode = 'count',
  emptyStateSuggestions,
  flowMode = 'directional',
  className,
}: ChordChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width, height } = useResizeObserver(containerRef);
  const instanceId = useId().replace(/[^a-zA-Z0-9-]/g, '');
  const [hover, setHover] = useState<HoverTarget>(null);

  const matrix = data?.matrix ?? [];
  const labels = data?.labels ?? [];
  const shareMatrix = data?.shareMatrix;
  const shareDirection: ChordShareDirection =
    data?.shareDirection ?? 'outflows';
  const palette = colors ?? defaultPlotColors;

  const hasFlows = hasMeaningfulFlows(matrix);
  // With 2+ selected entities but no flows between them, keep the ring so the
  // user can read which entities they selected and learn how to recover.
  const isEmptyStateWithNodes = labels.length > 1 && !hasFlows;
  const size = Math.min(width, height);
  const outerRadius = Math.max(size / 2 - CHORD_PADDING, 0);
  const innerRadius = Math.max(outerRadius - ARC_THICKNESS, 0);

  // One ribbon per non-zero matrix cell so A->B and B->A stay visually distinct.
  const chords: Chords | null =
    labels.length > 1 && hasFlows
      ? buildDirectedChords(matrix, PAD_ANGLE)
      : null;

  const emptyStateGroups: D3ChordGroup[] = isEmptyStateWithNodes
    ? buildEqualSizedGroups(labels.length, PAD_ANGLE)
    : [];

  const ribbonGenerator = ribbonFactory<D3Chord, ChordSubgroup>().radius(
    innerRadius
  );
  // d3's overloads infer `void` here even though a path string is returned.
  const getRibbonPath = (c: D3Chord): string | null =>
    (ribbonGenerator(c) as unknown as string | null) ?? null;

  const isPlotSizeValid = Number.isFinite(size) && size > 0 && innerRadius > 0;
  const canRenderChords = isPlotSizeValid && chords !== null;
  const canRenderEmptyState = isPlotSizeValid && isEmptyStateWithNodes;

  const isAnyHovered = hover !== null;

  // Paint highlighted elements last so they sit on top of faded ones.
  const orderedChords = chords
    ? [...chords].sort(
        (a, b) => relevanceWeight(a, hover) - relevanceWeight(b, hover)
      )
    : [];

  const orderedGroups = chords
    ? [...chords.groups].sort(
        (a, b) =>
          groupRelevanceWeight(a.index, hover, chords) -
          groupRelevanceWeight(b.index, hover, chords)
      )
    : [];

  const groupFlows = chords ? computeGroupInOutTotals(matrix) : null;

  const tickStep =
    showTickLabels && tickLabelMode === 'count' && chords
      ? computeNiceTickStep(maxGroupValue(chords.groups))
      : 0;
  const groupShareTotals =
    showTickLabels && tickLabelMode === 'share' && shareMatrix
      ? computeGroupShareTotals(shareMatrix, shareDirection)
      : null;
  const labelOffset = showTickLabels
    ? LABEL_OFFSET + TICK_RESERVED_OFFSET
    : LABEL_OFFSET;

  const labelWindows = chords
    ? computeLabelWindows({
        groups: chords.groups,
        labels,
        outerRadius,
        labelOffset,
        labelFontSize,
      })
    : null;

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      data-testid={plotTestId(ReplotChartNames.Chord)}
    >
      <PlotLoadWrapper loading={loading} noData={labels.length < 2}>
        {canRenderEmptyState ? (
          <ChordEmptyState
            groups={emptyStateGroups}
            labels={labels}
            palette={palette}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            labelFontSize={labelFontSize}
            size={size}
            instanceId={instanceId}
            suggestions={emptyStateSuggestions ?? []}
          />
        ) : null}
        {canRenderChords && chords ? (
          <svg
            className={styles.svg}
            viewBox={`0 0 ${size} ${size}`}
            preserveAspectRatio="xMidYMid meet"
          >
            <g transform={`translate(${size / 2}, ${size / 2})`}>
              {orderedChords.map((c) => {
                const gradientId = `chord-gradient-${instanceId}-${c.source.index}-${c.target.index}`;
                const flow = c.source.value;
                const share = shareMatrix?.[c.source.index]?.[c.target.index];
                const color =
                  palette[c.source.index % palette.length] ?? PRIMARY_COLOR;
                const isHovered = isRibbonHover(c, hover);
                const isRelevant = isRibbonRelevant(c, hover);
                const isFaded = isAnyHovered && !isRelevant;
                const isHighlighted = isAnyHovered && isRelevant;
                return (
                  <ChordRibbon
                    key={`ribbon-${c.source.index}-${c.target.index}`}
                    chord={c}
                    getRibbonPath={getRibbonPath}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    labelOffset={labelOffset}
                    labelFontSize={labelFontSize}
                    color={color}
                    gradientId={gradientId}
                    sourceLabel={labels[c.source.index] ?? ''}
                    targetLabel={labels[c.target.index] ?? ''}
                    flow={flow}
                    share={share}
                    shareDirection={shareDirection}
                    flowMode={flowMode}
                    isFaded={isFaded}
                    isHovered={isHovered}
                    isHighlighted={isHighlighted}
                    onHoverStart={() =>
                      setHover({
                        kind: 'ribbon',
                        sourceIndex: c.source.index,
                        targetIndex: c.target.index,
                      })
                    }
                    onHoverEnd={() => setHover(null)}
                  />
                );
              })}
              {orderedGroups.map((group) => {
                const color =
                  palette[group.index % palette.length] ?? PRIMARY_COLOR;
                const isHovered = isGroupHover(group.index, hover);
                const isFaded =
                  isAnyHovered && !isGroupRelevant(group.index, hover, chords);
                const ticks = showTickLabels
                  ? buildGroupTicks({
                      group,
                      mode: tickLabelMode,
                      step: tickStep,
                      groupShareTotal: groupShareTotals?.[group.index] ?? 0,
                    })
                  : [];
                return (
                  <ChordGroup
                    key={`group-${group.index}`}
                    group={group}
                    label={labels[group.index] ?? ''}
                    color={color}
                    inflow={groupFlows?.inflow[group.index] ?? 0}
                    outflow={groupFlows?.outflow[group.index] ?? 0}
                    flowMode={flowMode}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    labelOffset={labelOffset}
                    labelFontSize={labelFontSize}
                    labelPathId={`chord-label-${instanceId}-${group.index}`}
                    labelStartAngle={labelWindows?.get(group.index)?.startAngle}
                    labelEndAngle={labelWindows?.get(group.index)?.endAngle}
                    ticks={ticks}
                    tickFontSize={TICK_FONT_SIZE}
                    tickLineLength={TICK_LINE_LENGTH}
                    tickLabelOffset={TICK_LABEL_OFFSET}
                    isFaded={isFaded}
                    isHovered={isHovered}
                    onHoverStart={() =>
                      setHover({ kind: 'group', index: group.index })
                    }
                    onHoverEnd={() => setHover(null)}
                  />
                );
              })}
            </g>
          </svg>
        ) : null}
      </PlotLoadWrapper>
    </div>
  );
};

const EMPTY_STATE_OVERLAY_FRACTION = 0.85;

type ChordEmptyStateProps = {
  groups: D3ChordGroup[];
  labels: string[];
  palette: string[];
  innerRadius: number;
  outerRadius: number;
  labelFontSize: number;
  size: number;
  instanceId: string;
  suggestions: string[];
};

const ChordEmptyState = ({
  groups,
  labels,
  palette,
  innerRadius,
  outerRadius,
  labelFontSize,
  size,
  instanceId,
  suggestions,
}: ChordEmptyStateProps) => {
  const overlaySide = innerRadius * 2 * EMPTY_STATE_OVERLAY_FRACTION;
  const overlayHalf = overlaySide / 2;
  return (
    <svg
      className={styles.svg}
      viewBox={`0 0 ${size} ${size}`}
      preserveAspectRatio="xMidYMid meet"
      data-testid="chord-chart__empty-state"
    >
      <g transform={`translate(${size / 2}, ${size / 2})`}>
        {groups.map((group) => {
          const color = palette[group.index % palette.length] ?? PRIMARY_COLOR;
          return (
            <ChordGroup
              key={`empty-group-${group.index}`}
              group={group}
              label={labels[group.index] ?? ''}
              color={color}
              inflow={0}
              outflow={0}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              labelOffset={LABEL_OFFSET}
              labelFontSize={labelFontSize}
              labelPathId={`chord-empty-label-${instanceId}-${group.index}`}
              isFaded={false}
              isHovered={false}
              onHoverStart={() => undefined}
              onHoverEnd={() => undefined}
              interactive={false}
            />
          );
        })}
        <foreignObject
          x={-overlayHalf}
          y={-overlayHalf}
          width={overlaySide}
          height={overlaySide}
        >
          <div className={styles.emptyStateOverlay}>
            <p className={styles.emptyStateMessage}>
              No significant direct link exists between these selected entities.
            </p>
            {suggestions.length > 0 ? (
              <>
                <p className={styles.emptyStateSuggestionsLabel}>
                  Add one of these entities to see transitions:
                </p>
                <p className={styles.emptyStateSuggestionsList}>
                  {suggestions.join(', ')}
                </p>
              </>
            ) : null}
          </div>
        </foreignObject>
      </g>
    </svg>
  );
};

const isGroupHover = (index: number, hover: HoverTarget): boolean =>
  hover?.kind === 'group' && hover.index === index;

const isRibbonHover = (c: D3Chord, hover: HoverTarget): boolean =>
  hover?.kind === 'ribbon' &&
  hover.sourceIndex === c.source.index &&
  hover.targetIndex === c.target.index;

const isRibbonRelevant = (c: D3Chord, hover: HoverTarget): boolean => {
  if (hover === null) return true;
  if (hover.kind === 'group') {
    return c.source.index === hover.index || c.target.index === hover.index;
  }
  return (
    c.source.index === hover.sourceIndex && c.target.index === hover.targetIndex
  );
};

const isGroupRelevant = (
  groupIndex: number,
  hover: HoverTarget,
  chords: Chords
): boolean => {
  if (hover === null) return true;
  if (hover.kind === 'group') {
    if (hover.index === groupIndex) return true;
    return chords.some(
      (c) =>
        (c.source.index === hover.index && c.target.index === groupIndex) ||
        (c.target.index === hover.index && c.source.index === groupIndex)
    );
  }
  return hover.sourceIndex === groupIndex || hover.targetIndex === groupIndex;
};

const relevanceWeight = (c: D3Chord, hover: HoverTarget): number =>
  hover === null || isRibbonRelevant(c, hover) ? 1 : 0;

const groupRelevanceWeight = (
  groupIndex: number,
  hover: HoverTarget,
  chords: Chords
): number =>
  hover === null || isGroupRelevant(groupIndex, hover, chords) ? 1 : 0;

type LabelWindow = { startAngle: number; endAngle: number };

const computeLabelWindows = ({
  groups,
  labels,
  outerRadius,
  labelOffset,
  labelFontSize,
}: {
  groups: readonly D3ChordGroup[];
  labels: string[];
  outerRadius: number;
  labelOffset: number;
  labelFontSize: number;
}): Map<number, LabelWindow> => {
  const windows = new Map<number, LabelWindow>();
  const n = groups.length;
  if (n === 0) return windows;

  const mid = groups.map((g) => (g.startAngle + g.endAngle) / 2);
  const ownHalf = groups.map((g) => (g.endAngle - g.startAngle) / 2);
  const radius = mid.map((m) =>
    getLabelRadius({ midAngle: m, outerRadius, labelOffset, labelFontSize })
  );

  const approxRadius = outerRadius + labelOffset;
  const gapAngle =
    approxRadius > 0
      ? Math.min(PAD_ANGLE, LABEL_GAP_PX / approxRadius)
      : PAD_ANGLE;

  const want = groups.map((g, i) => {
    const textWidth = getTextWidth({
      text: labels[g.index] ?? '',
      fontSize: labelFontSize,
      fontFamily: LABEL_FONT_FAMILY,
    });
    // Without a measurement (e.g. no canvas) fall back to the node's own arc.
    if (textWidth == null) return ownHalf[i] ?? 0;
    return (textWidth / 2 + LABEL_DESIRED_PADDING_PX) / (radius[i] || 1);
  });
  // A label never needs more than its text wants, and its arc is always free.
  const floor = want.map((w, i) => Math.min(w, ownHalf[i] ?? 0));

  // D[i] is the max combined half-width the boundary between i and i+1 allows.
  const maxBoundary = groups.map((_, i) => {
    const next = (i + 1) % n;
    const delta =
      next === 0
        ? (mid[0] ?? 0) + 2 * Math.PI - (mid[i] ?? 0)
        : (mid[next] ?? 0) - (mid[i] ?? 0);
    return delta - gapAngle;
  });

  let half = floor.slice();
  for (let iter = 0; iter < LABEL_RELAX_ITERATIONS; iter++) {
    const nextHalf = half.slice();
    for (let i = 0; i < n; i++) {
      const right = (i + 1) % n;
      const left = (i - 1 + n) % n;
      const capRight = (maxBoundary[i] ?? 0) - (half[right] ?? 0);
      const capLeft = (maxBoundary[left] ?? 0) - (half[left] ?? 0);
      const target = Math.min(want[i] ?? 0, capRight, capLeft);
      const clamped = Math.max(floor[i] ?? 0, target);
      nextHalf[i] =
        (half[i] ?? 0) + LABEL_RELAX_DAMPING * (clamped - (half[i] ?? 0));
    }
    half = nextHalf;
  }

  // Final pass: hard-enforce each boundary so transient relaxation overshoot
  // can never leave two labels overlapping.
  for (let i = 0; i < n; i++) {
    const right = (i + 1) % n;
    const sum = (half[i] ?? 0) + (half[right] ?? 0);
    const limit = maxBoundary[i] ?? 0;
    if (sum > limit && sum > 0) {
      const scale = limit / sum;
      half[i] = Math.max(floor[i] ?? 0, (half[i] ?? 0) * scale);
      half[right] = Math.max(floor[right] ?? 0, (half[right] ?? 0) * scale);
    }
  }

  groups.forEach((g, i) => {
    const h = Math.max(0, half[i] ?? 0);
    windows.set(g.index, {
      startAngle: (mid[i] ?? 0) - h,
      endAngle: (mid[i] ?? 0) + h,
    });
  });
  return windows;
};

// Equal arcs share the ring evenly so the empty state still communicates the
// selected entities. Value is 0 because there are no flows to render.
const buildEqualSizedGroups = (n: number, padAngle: number): D3ChordGroup[] => {
  if (n < 2) return [];
  const totalPad = padAngle * n;
  const each = Math.max(0, (2 * Math.PI - totalPad) / n);
  return Array.from({ length: n }, (_, i) => {
    const startAngle = i * (each + padAngle);
    return {
      index: i,
      startAngle,
      endAngle: startAngle + each,
      value: 0,
    };
  });
};

const buildDirectedChords = (
  matrix: number[][],
  padAngle: number
): Chords | null => {
  const n = matrix.length;
  if (n < 2) return null;

  const groupTotals = computeGroupTotals(matrix);
  const totalWeight = groupTotals.reduce((s, v) => s + v, 0);
  if (totalWeight <= 0) return null;

  const k = Math.max(0, 2 * Math.PI - padAngle * n) / totalWeight;
  const outSub: (ChordSubgroup | undefined)[][] = Array.from(
    { length: n },
    () => []
  );
  const inSub: (ChordSubgroup | undefined)[][] = Array.from(
    { length: n },
    () => []
  );
  const groups: D3ChordGroup[] = [];

  let x = 0;
  for (let i = 0; i < n; i++) {
    const groupStart = x;
    const partners = orderPartnersByPairWeight(matrix, i);
    const outRow = outSub[i];
    const inRow = inSub[i];
    if (!outRow || !inRow) continue;

    for (const j of partners) {
      const outV = matrix[i]?.[j] ?? 0;
      if (outV > 0) {
        const a0 = x;
        x += outV * k;
        outRow[j] = {
          index: i,
          startAngle: a0,
          endAngle: x,
          value: outV,
        };
      }
      const inV = matrix[j]?.[i] ?? 0;
      if (inV > 0) {
        const a0 = x;
        x += inV * k;
        inRow[j] = {
          index: i,
          startAngle: a0,
          endAngle: x,
          value: inV,
        };
      }
    }

    groups.push({
      index: i,
      startAngle: groupStart,
      endAngle: x,
      value: groupTotals[i] ?? 0,
    });
    x += padAngle;
  }

  const chords: D3Chord[] = [];
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const source = outSub[i]?.[j];
      const target = inSub[j]?.[i];
      if (!source || !target) continue;
      chords.push({ source, target });
    }
  }

  return Object.assign(chords, { groups });
};

const orderPartnersByPairWeight = (matrix: number[][], i: number): number[] => {
  const n = matrix.length;
  const partners: number[] = [];
  for (let j = 0; j < n; j++) if (j !== i) partners.push(j);
  partners.sort((a, b) => {
    const wa = (matrix[i]?.[a] ?? 0) + (matrix[a]?.[i] ?? 0);
    const wb = (matrix[i]?.[b] ?? 0) + (matrix[b]?.[i] ?? 0);
    return wb - wa;
  });
  return partners;
};

const computeGroupTotals = (matrix: number[][]): number[] => {
  const n = matrix.length;
  return Array.from({ length: n }, (_, i) => {
    let total = 0;
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      total += (matrix[i]?.[j] ?? 0) + (matrix[j]?.[i] ?? 0);
    }
    return total;
  });
};

const computeGroupInOutTotals = (
  matrix: number[][]
): { inflow: number[]; outflow: number[] } => {
  const n = matrix.length;
  const inflow = Array.from({ length: n }, () => 0);
  const outflow = Array.from({ length: n }, () => 0);
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const v = matrix[i]?.[j] ?? 0;
      outflow[i] = (outflow[i] ?? 0) + v;
      inflow[j] = (inflow[j] ?? 0) + v;
    }
  }
  return { inflow, outflow };
};

const maxGroupValue = (groups: readonly D3ChordGroup[]): number =>
  groups.reduce((m, g) => (g.value > m ? g.value : m), 0);

const SHARE_INTERMEDIATE_FRACTIONS = [0.25, 0.5, 0.75] as const;

const buildGroupTicks = ({
  group,
  mode,
  step,
  groupShareTotal,
}: {
  group: D3ChordGroup;
  mode: ChordTickLabelMode;
  step: number;
  groupShareTotal: number;
}): GroupTick[] => {
  const span = group.endAngle - group.startAngle;
  if (group.value <= 0 || span <= 0) return [];

  if (mode === 'share') {
    if (groupShareTotal <= 0) return [];
    // Labels are cumulative share for this node, scaled to its total — not
    // 0–100% of the arc — so an arc capturing 60% of a node reads 0 / 15 /
    // 30 / 45 / 60%.
    const fractions = [0, ...SHARE_INTERMEDIATE_FRACTIONS, 1] as const;
    return fractions.map((f) => ({
      angle: group.startAngle + f * span,
      label: sharePercentFormat(f * groupShareTotal),
    }));
  }

  const startTick: GroupTick = {
    angle: group.startAngle,
    label: formatTickValue(0),
  };
  const endTick: GroupTick = {
    angle: group.endAngle,
    label: formatTickValue(group.value),
  };
  if (step <= 0) return [startTick, endTick];

  const k = span / group.value;
  // Skip intermediates that would crowd the end-boundary tick.
  const maxIntermediate = group.value - step * 0.4;
  const intermediateCount = Math.max(0, Math.floor(maxIntermediate / step));
  const intermediates: GroupTick[] = Array.from(
    { length: intermediateCount },
    (_, idx) => {
      const v = step * (idx + 1);
      return {
        angle: v * k + group.startAngle,
        label: formatTickValue(v),
      };
    }
  );
  return [startTick, ...intermediates, endTick];
};

// outflows: row i sums i's outflow shares. inflows: column i sums i's inflow shares.
const computeGroupShareTotals = (
  shareMatrix: number[][],
  direction: ChordShareDirection
): number[] => {
  const n = shareMatrix.length;
  return Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) => {
      if (i === j) return 0;
      return direction === 'outflows'
        ? (shareMatrix[i]?.[j] ?? 0)
        : (shareMatrix[j]?.[i] ?? 0);
    }).reduce((a, b) => a + b, 0)
  );
};

const formatTickValue = (value: number): string => {
  if (value < 1000) return Math.round(value).toString();
  if (value < 1_000_000) {
    const v = value / 1000;
    return `${stripTrailingZeros(v.toFixed(1))}K`;
  }
  if (value < 1_000_000_000) {
    const v = value / 1_000_000;
    return `${stripTrailingZeros(v.toFixed(1))}M`;
  }
  const v = value / 1_000_000_000;
  return `${stripTrailingZeros(v.toFixed(1))}B`;
};

const stripTrailingZeros = (numStr: string): string =>
  numStr.replace(/\.0+$/, '');

// Round step to 1/2/5 × 10^n so the largest group lands near
// TARGET_TICKS_PER_LARGEST_GROUP intermediate ticks.
const computeNiceTickStep = (maxValue: number): number => {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return 0;
  const target = maxValue / TARGET_TICKS_PER_LARGEST_GROUP;
  const magnitude = 10 ** Math.floor(Math.log10(target));
  const normalized = target / magnitude;
  if (normalized < 1.5) return magnitude;
  if (normalized < 3.5) return 2 * magnitude;
  if (normalized < 7.5) return 5 * magnitude;
  return 10 * magnitude;
};
